import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ACTIVITY_LOG,
  STATUS_BADGE_CLASS,
  TRAINING_REJECTION_REASON,
  buildTimeline,
  getTrainingStatus,
} from "./trainingData";
import "./training.css";

/* Training Request detail — ported from the prototype (view-training.html).
   Status-aware: badge, rejection banner, and the Approval Timeline adapt to
   the request status. Details / Logs tabs match the Travel detail pattern. */

const LOG_BADGE = {
  info: "log-badge log-badge-info",
  amend: "log-badge log-badge-amend",
  reject: "log-badge log-badge-reject",
  muted: "log-badge log-badge-muted",
  approved: "status-badge status-active",
};

const TrainingDetailPage = () => {
  const { id } = useParams();
  const requestId = (id || "TR-2026-062").trim();
  const status = getTrainingStatus(requestId);
  const [activeTab, setActiveTab] = useState("details");
  const timeline = useMemo(() => buildTimeline(status), [status]);

  return (
    <div className="bes-training bes-training-detail">
      <div className="training-page-head">
        <div className="title-left">
          <div className="page-icon training-icon">
            <i className="ri-graduation-cap-fill" />
          </div>
          <div>
            <h1>Training Request Details</h1>
            <p>Review the submitted training request information and approval progress.</p>
          </div>
        </div>
        <Link to="/training/request" className="btn btn-outline">
          <i className="ri-arrow-left-line" /> Back to Training
        </Link>
      </div>

      {status === "Rejected" && (
        <div className="warning-alert alert-danger rejection-banner">
          <i className="ri-error-warning-fill" />
          <p>
            <strong>Request rejected.</strong> {TRAINING_REJECTION_REASON}
          </p>
        </div>
      )}

      <div className="custom-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === "logs" ? "active" : ""}`}
          onClick={() => setActiveTab("logs")}
        >
          Logs
        </button>
      </div>

      {activeTab === "details" ? (
        <div className="detail-layout">
          <div className="training-detail-stack">
            {/* 1. Applicant Information */}
            <div className="panel">
              <div className="panel-header">
                <div className="detail-section-title">
                  <span>1</span>
                  <h2 className="panel-title">Applicant Information</h2>
                </div>
                <span className={`status-badge ${STATUS_BADGE_CLASS[status] || "status-approved"}`}>{status}</span>
              </div>
              <div className="panel-body">
                <div className="info-grid info-grid-four">
                  <div className="info-item">
                    <label>Employee Name</label>
                    <div>Eldren Aniell</div>
                  </div>
                  <div className="info-item">
                    <label>Date of Request</label>
                    <div>21 July 2026</div>
                  </div>
                  <div className="info-item">
                    <label>Department</label>
                    <div>CAPACITY &amp; DIGITALISATION</div>
                  </div>
                  <div className="info-item">
                    <label>Designation</label>
                    <div>Manager</div>
                  </div>
                </div>
                <div className="training-balance-summary">
                  <span>Training Budget Balance</span>
                  <strong>RM 5,000.00</strong>
                </div>
              </div>
            </div>

            {/* 2. Course Details */}
            <div className="panel">
              <div className="panel-header">
                <div className="detail-section-title">
                  <span>2</span>
                  <h2 className="panel-title">Course Details</h2>
                </div>
              </div>
              <div className="panel-body">
                <div className="detail-choice-grid">
                  <div>
                    <span>HRDF Claimable?</span>
                    <strong>Yes</strong>
                  </div>
                  <div>
                    <span>Training Location</span>
                    <strong>Within Malaysia</strong>
                  </div>
                  <div>
                    <span>Travel Required?</span>
                    <strong>No</strong>
                  </div>
                </div>
                <div className="info-grid detail-course-grid">
                  <div className="info-item">
                    <label>Course Name</label>
                    <div>Successful Project Management</div>
                  </div>
                  <div className="info-item">
                    <label>Training Provider</label>
                    <div>Malaysian Institute of Management</div>
                  </div>
                  <div className="info-item info-span-full">
                    <label>Course Brief Description</label>
                    <div>Practical project planning, delivery, risk management, and stakeholder coordination.</div>
                  </div>
                  <div className="info-item">
                    <label>Course Dates</label>
                    <div>11–14 November 2024</div>
                  </div>
                  <div className="info-item">
                    <label>Number of Day(s)</label>
                    <div>4 days</div>
                  </div>
                  <div className="info-item">
                    <label>Completion Duration</label>
                    <div>28 hours</div>
                  </div>
                  <div className="info-item">
                    <label>Venue</label>
                    <div>Malaysian Institute of Management, Kuala Lumpur</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Estimated Costing */}
            <div className="panel">
              <div className="panel-header">
                <div className="detail-section-title">
                  <span>3</span>
                  <h2 className="panel-title">Estimated Costing (RM)</h2>
                </div>
              </div>
              <div className="panel-body">
                <div className="detail-cost-grid">
                  <div>
                    <span>Course Fee</span>
                    <strong>RM 1,200.00</strong>
                  </div>
                  <div>
                    <span>Local Transportation</span>
                    <strong>RM 0.00</strong>
                  </div>
                  <div>
                    <span>Meal Allowance</span>
                    <strong>RM 0.00</strong>
                  </div>
                  <div>
                    <span>Miscellaneous</span>
                    <strong>RM 0.00</strong>
                  </div>
                  <div className="detail-cost-total">
                    <span>Total Estimated Cost</span>
                    <strong>RM 1,200.00</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Training Reason(s) */}
            <div className="panel">
              <div className="panel-header">
                <div className="detail-section-title">
                  <span>4</span>
                  <h2 className="panel-title">Training Reason(s)</h2>
                </div>
              </div>
              <div className="panel-body">
                <div className="info-item">
                  <label>Benefit to role and organisation</label>
                  <div>
                    To strengthen project management knowledge and improve planning, delivery, and stakeholder
                    coordination across internal initiatives.
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Supporting Documents */}
            <div className="panel">
              <div className="panel-header">
                <div className="detail-section-title">
                  <span>5</span>
                  <h2 className="panel-title">Supporting Documents</h2>
                </div>
              </div>
              <div className="panel-body">
                {[
                  { name: "Course content & timetable.pdf", meta: "PDF · 1.2 MB", icon: "ri-file-pdf-2-line" },
                  { name: "Trainer's profile.pdf", meta: "PDF · 930 KB", icon: "ri-file-pdf-2-line" },
                  { name: "Course fee quotation.pdf", meta: "PDF · 840 KB", icon: "ri-file-text-line" },
                ].map((file) => (
                  <div className="attachment-row" key={file.name}>
                    <div className="attachment-left">
                      <div className="attachment-icon">
                        <i className={file.icon} />
                      </div>
                      <div>
                        <strong>{file.name}</strong>
                        <small>{file.meta}</small>
                      </div>
                    </div>
                    <button type="button" className="btn btn-outline">
                      Preview
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside>
            <div className="summary-card">
              <h3>Cost Summary</h3>
              <div className="cost-row">
                <span>Course Fee</span>
                <span>RM 1,200.00</span>
              </div>
              <div className="cost-row">
                <span>Local Transportation</span>
                <span>RM 0.00</span>
              </div>
              <div className="cost-row">
                <span>Meal Allowance</span>
                <span>RM 0.00</span>
              </div>
              <div className="cost-row">
                <span>Miscellaneous</span>
                <span>RM 0.00</span>
              </div>
              <div className="cost-row">
                <span>Total Estimated Cost</span>
                <span>RM 1,200.00</span>
              </div>
            </div>

            <div className="summary-card">
              <h3>Approval Timeline</h3>
              <div className="timeline">
                {timeline.map((step) => (
                  <div className={`timeline-item is-${step.state}`} key={step.title}>
                    <span className="timeline-dot" />
                    <div className="timeline-title">{step.title}</div>
                    <div className="timeline-meta">{step.meta}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="notice">
              <strong>Note:</strong> Training evaluation is required after completion of the approved training program.
            </div>
          </aside>
        </div>
      ) : (
        <div className="panel">
          <div className="panel-header">
            <div className="detail-section-title">
              <h2 className="panel-title">Activity Log</h2>
            </div>
            <span className="header-note-light">All changes, approvals, rejections, and amendments — newest first</span>
          </div>
          <div className="panel-body no-pad">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-17">Date / Time</th>
                    <th className="w-21">By</th>
                    <th className="w-14">Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {ACTIVITY_LOG.map((entry) => (
                    <tr key={`${entry.time}-${entry.action}`}>
                      <td className="text-muted">{entry.time}</td>
                      <td className="font-medium">
                        {entry.by} <small className="text-muted">({entry.role})</small>
                      </td>
                      <td>
                        <span className={LOG_BADGE[entry.badge] || LOG_BADGE.muted}>{entry.action}</span>
                      </td>
                      <td>{entry.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDetailPage;
